import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Fab,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Checkbox
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  AssignmentOutlined,
  GroupOutlined,
  CheckCircleOutlined,
  BarChart as BarChartIcon,
  Add as AddIcon,
  Close as CloseIcon,
  DragIndicator as DragIndicatorIcon,
  AccessTime as DigitalClockIcon,
  Schedule as AnalogClockIcon,
  StickyNote2Outlined as NotesIcon,
  ChecklistOutlined as TodoIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { Responsive, WidthProvider } from 'react-grid-layout/legacy';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { createApiUrl } from '../utils/apiConfig';
import { getAuthHeaders } from '../utils/authUtils';
import { useThemeContext } from '../theme/CustomThemeProvider';
import MyTasks from './reporting/MyTasks';
import TeamTasks from './reporting/TeamTasks';
import ApprovalWorkflow from './reporting/ApprovalWorkflow';
import PersonalStatsWidget from './PersonalStatsWidget';

const ResponsiveGridLayout = WidthProvider(Responsive);

const WIDGET_IDS = {
  MY_TASKS: 'myTasks',
  TEAM_TASKS: 'teamTasks',
  APPROVAL_NEW: 'approvalNewExams',
  PERSONAL_STATS: 'personalStats',
  DIGITAL_CLOCK: 'digitalClock',
  ANALOG_CLOCK: 'analogClock',
  NOTES: 'notes',
  TODO: 'todo'
};

const GRID_BREAKPOINTS = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 };
const GRID_COLS = { lg: 24, md: 20, sm: 12, xs: 8, xxs: 4 };
const BP_KEYS = ['lg', 'md', 'sm', 'xs', 'xxs'];

const FINE_GRID_ROW_HEIGHT = 12;
const LEGACY_GRID_COLS = { lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 };
const LEGACY_ROW_HEIGHT = 24;

const WIDGET_SIZE_PRESETS = {
  [WIDGET_IDS.DIGITAL_CLOCK]:  { wFrac: 1/6, minWFrac: 1/8, hLegacy: 5 },
  [WIDGET_IDS.ANALOG_CLOCK]:   { wFrac: 1/7, minWFrac: 1/8, hLegacy: 7 },
  [WIDGET_IDS.NOTES]:          { wFrac: 1/5, minWFrac: 1/6, hLegacy: 5 },
  [WIDGET_IDS.TODO]:           { wFrac: 1/5, minWFrac: 1/6, hLegacy: 7 },
  [WIDGET_IDS.PERSONAL_STATS]: { wFrac: 1/3, minWFrac: 1/4, hLegacy: 10 },
};

const defaultItemForBreakpoint = (id, bp) => {
  const cols = GRID_COLS[bp] || 24;
  const preset = WIDGET_SIZE_PRESETS[id];
  if (preset) {
    const w = Math.min(Math.max(2, Math.round(cols * preset.wFrac)), cols);
    const minW = Math.min(Math.max(2, Math.round(cols * preset.minWFrac)), cols);
    const h = Math.round((preset.hLegacy * LEGACY_ROW_HEIGHT) / FINE_GRID_ROW_HEIGHT);
    return { i: id, x: 0, y: 0, w, h, minW };
  }
  const w = Math.min(Math.max(1, Math.floor(cols / 2)), cols);
  const minW = Math.min(Math.max(1, Math.floor(cols / 4)), cols);
  const h = Math.round((18 * LEGACY_ROW_HEIGHT) / FINE_GRID_ROW_HEIGHT);
  return { i: id, x: 0, y: 0, w, h, minW };
};

const migrateLayoutsCoarseToFine = (layoutsObj) => {
  if (!layoutsObj || typeof layoutsObj !== 'object') return {};
  const out = {};
  for (const bp of BP_KEYS) {
    const oldCols = LEGACY_GRID_COLS[bp] || 12;
    const newCols = GRID_COLS[bp] || 12;
    const colScale = newCols / oldCols;
    const rowScale = LEGACY_ROW_HEIGHT / FINE_GRID_ROW_HEIGHT;
    out[bp] = (layoutsObj[bp] || []).map((item) => {
      if (!item || typeof item !== 'object') return item;
      let x = Math.round(Number(item.x) * colScale);
      let w = Math.max(1, Math.round(Number(item.w) * colScale));
      let y = Math.round(Number(item.y) * rowScale);
      let h = Math.max(1, Math.round(Number(item.h) * rowScale));
      if (x + w > newCols) w = Math.max(1, newCols - x);
      if (x >= newCols) x = Math.max(0, newCols - w);
      const { minH: _mh, minW: oldMinW, ...rest } = item;
      const next = { ...rest, x, y, w, h };
      if (oldMinW != null && !Number.isNaN(Number(oldMinW))) {
        next.minW = Math.min(Math.max(1, Math.round(Number(oldMinW) * colScale)), newCols);
      }
      return next;
    });
  }
  return out;
};

const stripLayoutMinHeights = (layoutsObj) => {
  if (!layoutsObj || typeof layoutsObj !== 'object') return {};
  const out = {};
  for (const bp of BP_KEYS) {
    out[bp] = (layoutsObj[bp] || []).map((item) => {
      if (!item || typeof item !== 'object') return item;
      const { minH: _removed, ...rest } = item;
      return rest;
    });
  }
  return out;
};

const mergeNewLayoutItem = (prevLayouts, widgetId) => {
  const next = { ...(prevLayouts || {}) };
  for (const bp of BP_KEYS) {
    const list = [...(next[bp] || [])];
    if (list.some((it) => it.i === widgetId)) continue;
    const maxY = list.length ? Math.max(...list.map((it) => it.y + it.h)) : 0;
    const base = defaultItemForBreakpoint(widgetId, bp);
    list.push({ ...base, y: maxY });
    next[bp] = list;
  }
  return next;
};

const removeLayoutItem = (prevLayouts, widgetId) => {
  const next = { ...(prevLayouts || {}) };
  for (const bp of BP_KEYS) {
    next[bp] = (next[bp] || []).filter((it) => it.i !== widgetId);
  }
  return next;
};

const keyframes = {
  fadeInUp: `
    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }
  `,
  waveMove1: `
    @keyframes waveMove1 {
      0%, 100% { transform: translateX(0); }
      50% { transform: translateX(-100px); }
    }
  `,
  waveMove2: `
    @keyframes waveMove2 {
      0%, 100% { transform: translateX(0); }
      50% { transform: translateX(80px); }
    }
  `,
  waveMove3: `
    @keyframes waveMove3 {
      0%, 100% { transform: translateX(0); }
      50% { transform: translateX(-60px); }
    }
  `
};

const StyleInjector = () => {
  useEffect(() => {
    const styleId = 'dashboard-animations';
    if (!document.getElementById(styleId)) {
      const styleSheet = document.createElement('style');
      styleSheet.id = styleId;
      styleSheet.textContent = Object.values(keyframes).join('\n');
      document.head.appendChild(styleSheet);
    }
  }, []);
  return null;
};

function WidgetBodySizer({ widgetId, children }) {
  const wrapRef = useRef(null);
  const [height, setHeight] = useState(360);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => {
      setHeight(Math.max(120, el.clientHeight));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [widgetId]);

  return (
    <Box
      ref={wrapRef}
      sx={{
        flex: 1,
        minHeight: 0,
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {React.isValidElement(children)
        ? React.cloneElement(children, { widgetBodyHeight: height })
        : children}
    </Box>
  );
}

/* ─── Digital Clock Widget ─── */
function DigitalClockWidget() {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const timeStr = now.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const dateStr = now.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 0.5, userSelect: 'none' }}>
      <Typography variant="h2" sx={{ fontWeight: 300, fontFamily: "'Roboto Mono', monospace", letterSpacing: 2, color: 'var(--rd-primary)' }}>
        {timeStr}
      </Typography>
      <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 400 }}>
        {dateStr}
      </Typography>
    </Box>
  );
}

/* ─── Analog Clock Widget ─── */
function AnalogClockWidget() {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const s = now.getSeconds();
  const m = now.getMinutes();
  const h = now.getHours() % 12;
  const secAngle = s * 6;
  const minAngle = m * 6 + s * 0.1;
  const hrAngle = h * 30 + m * 0.5;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', userSelect: 'none' }}>
      <svg viewBox="0 0 200 200" style={{ width: '100%', maxWidth: 220, maxHeight: '100%' }}>
        <circle cx="100" cy="100" r="95" fill="none" stroke="currentColor" strokeWidth="2" opacity={0.15} />
        {[...Array(12)].map((_, i) => {
          const angle = (i * 30 - 90) * (Math.PI / 180);
          const x1 = 100 + 82 * Math.cos(angle);
          const y1 = 100 + 82 * Math.sin(angle);
          const x2 = 100 + 90 * Math.cos(angle);
          const y2 = 100 + 90 * Math.sin(angle);
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="currentColor" strokeWidth={i % 3 === 0 ? 3 : 1.5} opacity={0.5} />;
        })}
        {/* Hour hand */}
        <line
          x1="100" y1="100"
          x2={100 + 50 * Math.cos((hrAngle - 90) * Math.PI / 180)}
          y2={100 + 50 * Math.sin((hrAngle - 90) * Math.PI / 180)}
          stroke="currentColor" strokeWidth="4" strokeLinecap="round"
        />
        {/* Minute hand */}
        <line
          x1="100" y1="100"
          x2={100 + 68 * Math.cos((minAngle - 90) * Math.PI / 180)}
          y2={100 + 68 * Math.sin((minAngle - 90) * Math.PI / 180)}
          stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
        />
        {/* Second hand */}
        <line
          x1="100" y1="100"
          x2={100 + 75 * Math.cos((secAngle - 90) * Math.PI / 180)}
          y2={100 + 75 * Math.sin((secAngle - 90) * Math.PI / 180)}
          stroke="var(--rd-primary)" strokeWidth="1.2" strokeLinecap="round"
        />
        <circle cx="100" cy="100" r="4" fill="var(--rd-primary)" />
      </svg>
    </Box>
  );
}

/* ─── Notes Widget ─── */
function NotesWidget({ value, onChange }) {
  const { t } = useTranslation();
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 1 }}>
      <TextField
        multiline
        fullWidth
        variant="standard"
        placeholder={t('dashboard.widgets.notes.placeholder', 'Notiz eingeben…')}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        InputProps={{ disableUnderline: true, sx: { fontSize: 14, lineHeight: 1.6 } }}
        sx={{ flex: 1, '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start' }, '& textarea': { height: '100% !important', overflow: 'auto !important' } }}
      />
    </Box>
  );
}

/* ─── To-Do Widget ─── */
function TodoWidget({ items, onChange }) {
  const { t } = useTranslation();
  const [input, setInput] = useState('');

  const addItem = () => {
    const text = input.trim();
    if (!text) return;
    onChange([...(items || []), { id: Date.now(), text, done: false }]);
    setInput('');
  };

  const toggleItem = (id) => {
    onChange((items || []).map((it) => (it.id === id ? { ...it, done: !it.done } : it)));
  };

  const removeItem = (id) => {
    onChange((items || []).filter((it) => it.id !== id));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); addItem(); }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', gap: 1, px: 1, pt: 1, pb: 0.5, flexShrink: 0 }}>
        <TextField
          size="small"
          fullWidth
          variant="outlined"
          placeholder={t('dashboard.widgets.todo.placeholder', 'Neue Aufgabe…')}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          sx={{ '& .MuiOutlinedInput-root': { fontSize: 13 } }}
        />
        <Button variant="contained" size="small" onClick={addItem} disabled={!input.trim()} sx={{ minWidth: 'auto', px: 1.5, bgcolor: 'var(--rd-primary)', '&:hover': { bgcolor: 'var(--rd-primary-hover)' } }}>
          <AddIcon fontSize="small" />
        </Button>
      </Box>
      <List dense sx={{ flex: 1, overflow: 'auto', py: 0 }}>
        {(!items || items.length === 0) && (
          <ListItem>
            <ListItemText secondary={t('dashboard.widgets.todo.empty', 'Keine Aufgaben vorhanden')} sx={{ textAlign: 'center' }} />
          </ListItem>
        )}
        {(items || []).map((item) => (
          <ListItem key={item.id} dense disablePadding sx={{ px: 1 }}
            secondaryAction={
              <IconButton edge="end" size="small" onClick={() => removeItem(item.id)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            }
          >
            <Checkbox
              edge="start"
              size="small"
              checked={item.done}
              onChange={() => toggleItem(item.id)}
              sx={{ p: 0.5, mr: 0.5, '&.Mui-checked': { color: 'var(--rd-primary)' } }}
            />
            <ListItemText
              primary={item.text}
              sx={{ '& .MuiListItemText-primary': { textDecoration: item.done ? 'line-through' : 'none', opacity: item.done ? 0.5 : 1, fontSize: 13 } }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );
}

/* ─── Dashboard Component ─── */
const Dashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { mode } = useThemeContext();
  const isDark = mode === 'dark';

  const [currentUser, setCurrentUser] = useState(null);
  const [approvalWorkflowEnabled, setApprovalWorkflowEnabled] = useState(false);
  const [widgets, setWidgets] = useState([]);
  const [layouts, setLayouts] = useState({});
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [notesText, setNotesText] = useState('');
  const [todoItems, setTodoItems] = useState([]);
  const persistTimer = useRef(null);
  const dbLoaded = useRef(false);

  const fetchCurrentUser = async () => {
    try {
      const response = await fetch(createApiUrl('auth/me'), { headers: getAuthHeaders() });
      if (response.ok) {
        const userData = await response.json();
        setCurrentUser(userData);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Benutzerdaten:', err);
    }
  };

  const fetchServerConfig = useCallback(async () => {
    try {
      const response = await fetch(createApiUrl('config/format'), { headers: getAuthHeaders() });
      if (response.ok) {
        const config = await response.json();
        setApprovalWorkflowEnabled(config.use_approval_workflow === 'true');
      }
    } catch (error) {
      console.warn('Could not load server config:', error);
    }
  }, []);

  useEffect(() => {
    fetchCurrentUser();
    fetchServerConfig();
  }, [fetchServerConfig]);

  /* ── Load from DB ── */
  useEffect(() => {
    if (!currentUser?.id) return;
    const loadFromDb = async () => {
      try {
        const res = await fetch(createApiUrl('profile/dashboard-settings'), { headers: getAuthHeaders() });
        if (!res.ok) return;
        const data = await res.json();
        const settings = data.dashboard_settings;
        if (settings && typeof settings === 'object') {
          let loadedLayouts = settings.layouts && typeof settings.layouts === 'object'
            ? stripLayoutMinHeights(settings.layouts) : {};
          const loadedWidgets = Array.isArray(settings.widgets) ? settings.widgets : [];
          for (const id of loadedWidgets) {
            const needs = BP_KEYS.some((bp) => !(loadedLayouts[bp] || []).some((it) => it.i === id));
            if (needs) loadedLayouts = mergeNewLayoutItem(loadedLayouts, id);
          }
          setLayouts(loadedLayouts);
          setWidgets(loadedWidgets);
          if (settings.notesText != null) setNotesText(settings.notesText);
          if (Array.isArray(settings.todoItems)) setTodoItems(settings.todoItems);
        } else {
          // Fallback: try localStorage for migration
          const lsKey = `rd_dashboard_widgets_v2_${currentUser.id}`;
          const raw = localStorage.getItem(lsKey);
          if (raw) {
            try {
              const parsed = JSON.parse(raw);
              const loadedWidgets = Array.isArray(parsed.widgets) ? parsed.widgets : [];
              let loadedLayouts = parsed.layouts && typeof parsed.layouts === 'object'
                ? stripLayoutMinHeights(parsed.layouts) : {};
              for (const id of loadedWidgets) {
                const needs = BP_KEYS.some((bp) => !(loadedLayouts[bp] || []).some((it) => it.i === id));
                if (needs) loadedLayouts = mergeNewLayoutItem(loadedLayouts, id);
              }
              setLayouts(loadedLayouts);
              setWidgets(loadedWidgets);
            } catch (e) { /* ignore */ }
          }
        }
      } catch (e) {
        console.warn('Dashboard settings load failed', e);
      }
      dbLoaded.current = true;
    };
    loadFromDb();
  }, [currentUser?.id]);

  /* ── Persist to DB (debounced) ── */
  const persistToDb = useCallback((nextLayouts, nextWidgets, nextNotes, nextTodos) => {
    if (!currentUser?.id) return;
    const payload = {
      dashboard_settings: {
        layouts: nextLayouts,
        widgets: nextWidgets,
        notesText: nextNotes,
        todoItems: nextTodos
      }
    };
    fetch(createApiUrl('profile/dashboard-settings'), {
      method: 'PUT',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).catch((e) => console.warn('Dashboard persist failed', e));
  }, [currentUser?.id]);

  const schedulePersist = useCallback(
    (nextLayouts, nextWidgets, nextNotes, nextTodos) => {
      if (persistTimer.current) clearTimeout(persistTimer.current);
      persistTimer.current = setTimeout(() => {
        persistToDb(nextLayouts, nextWidgets, nextNotes, nextTodos);
      }, 600);
    },
    [persistToDb]
  );

  useEffect(() => () => {
    if (persistTimer.current) clearTimeout(persistTimer.current);
  }, []);

  const hasApprovalPermission = currentUser && currentUser.can_approve === true;
  const showApprovalWidgetOption = approvalWorkflowEnabled && hasApprovalPermission;

  const handleLayoutChange = useCallback(
    (_, allLayouts) => {
      const cleaned = stripLayoutMinHeights(allLayouts);
      setLayouts(cleaned);
      schedulePersist(cleaned, widgets, notesText, todoItems);
    },
    [widgets, notesText, todoItems, schedulePersist]
  );

  const addWidget = useCallback(
    (id) => {
      if (widgets.includes(id)) return;
      const nextWidgets = [...widgets, id];
      setWidgets(nextWidgets);
      setLayouts((prev) => {
        const merged = mergeNewLayoutItem(prev, id);
        persistToDb(merged, nextWidgets, notesText, todoItems);
        return merged;
      });
      setAddDialogOpen(false);
    },
    [widgets, notesText, todoItems, persistToDb]
  );

  const removeWidget = useCallback(
    (id) => {
      const nextWidgets = widgets.filter((w) => w !== id);
      setWidgets(nextWidgets);
      setLayouts((prev) => {
        const next = removeLayoutItem(prev, id);
        persistToDb(next, nextWidgets, notesText, todoItems);
        return next;
      });
    },
    [widgets, notesText, todoItems, persistToDb]
  );

  const handleNotesChange = useCallback((val) => {
    setNotesText(val);
    schedulePersist(layouts, widgets, val, todoItems);
  }, [layouts, widgets, todoItems, schedulePersist]);

  const handleTodoChange = useCallback((val) => {
    setTodoItems(val);
    schedulePersist(layouts, widgets, notesText, val);
  }, [layouts, widgets, notesText, schedulePersist]);

  const getFullName = (user) => {
    if (!user) return '...';
    const parts = [];
    if (user.academic_degree) parts.push(user.academic_degree);
    if (user.first_name) parts.push(user.first_name);
    if (user.last_name) parts.push(user.last_name);
    return parts.length > 0 ? parts.join(' ') : user.username || '...';
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t('dashboard.greeting.morning');
    if (hour < 18) return t('dashboard.greeting.afternoon');
    return t('dashboard.greeting.evening');
  };

  const widgetTitle = (id) => {
    const key = `dashboard.widgets.titles.${id}`;
    const translated = t(key);
    return translated !== key ? translated : id;
  };

  const renderWidgetInner = (id) => {
    if (id === WIDGET_IDS.MY_TASKS) return <MyTasks embedAsWidget />;
    if (id === WIDGET_IDS.TEAM_TASKS) return <TeamTasks embedAsWidget />;
    if (id === WIDGET_IDS.APPROVAL_NEW) return <ApprovalWorkflow variant="newExamsWidget" />;
    if (id === WIDGET_IDS.DIGITAL_CLOCK) return <DigitalClockWidget />;
    if (id === WIDGET_IDS.ANALOG_CLOCK) return <AnalogClockWidget />;
    if (id === WIDGET_IDS.NOTES) return <NotesWidget value={notesText} onChange={handleNotesChange} />;
    if (id === WIDGET_IDS.TODO) return <TodoWidget items={todoItems} onChange={handleTodoChange} />;
    if (id === WIDGET_IDS.PERSONAL_STATS) return <PersonalStatsWidget />;
    return null;
  };

  const isEmpty = widgets.length === 0;

  /* ── All available widgets for the dialog ── */
  const allWidgetOptions = useMemo(() => {
    const list = [
      { id: WIDGET_IDS.MY_TASKS, icon: <AssignmentOutlined sx={{ color: 'var(--rd-primary)' }} /> },
      { id: WIDGET_IDS.TEAM_TASKS, icon: <GroupOutlined sx={{ color: 'var(--rd-primary)' }} /> },
    ];
    if (showApprovalWidgetOption) {
      list.push({ id: WIDGET_IDS.APPROVAL_NEW, icon: <CheckCircleOutlined sx={{ color: 'var(--rd-primary)' }} /> });
    }
    list.push(
      { id: WIDGET_IDS.PERSONAL_STATS, icon: <BarChartIcon sx={{ color: 'var(--rd-primary)' }} /> },
      { id: WIDGET_IDS.DIGITAL_CLOCK, icon: <DigitalClockIcon sx={{ color: 'var(--rd-primary)' }} /> },
      { id: WIDGET_IDS.ANALOG_CLOCK, icon: <AnalogClockIcon sx={{ color: 'var(--rd-primary)' }} /> },
      { id: WIDGET_IDS.NOTES, icon: <NotesIcon sx={{ color: 'var(--rd-primary)' }} /> },
      { id: WIDGET_IDS.TODO, icon: <TodoIcon sx={{ color: 'var(--rd-primary)' }} /> },
    );
    return list;
  }, [showApprovalWidgetOption]);

  return (
    <>
      <StyleInjector />
      <Box
        sx={{
          position: 'fixed',
          top: 64,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'var(--rd-bg-default)',
          overflow: 'hidden',
          zIndex: -1
        }}
      >
        <svg
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '120%',
            marginLeft: '-10%',
            height: '200px',
            pointerEvents: 'none'
          }}
          viewBox="0 0 1440 200"
          preserveAspectRatio="none"
        >
          <path
            d="M0,160 C320,220 420,100 720,140 C1020,180 1200,80 1440,120 L1440,200 L0,200 Z"
            fill={isDark ? 'color-mix(in srgb, var(--rd-primary) 8%, transparent)' : 'color-mix(in srgb, var(--rd-primary) 6%, transparent)'}
            style={{ animation: 'waveMove1 2.5s ease-in-out infinite' }}
          />
          <path
            d="M0,180 C240,140 480,200 720,160 C960,120 1200,180 1440,150 L1440,200 L0,200 Z"
            fill={isDark ? 'color-mix(in srgb, var(--rd-primary) 12%, transparent)' : 'color-mix(in srgb, var(--rd-primary) 10%, transparent)'}
            style={{ animation: 'waveMove2 3s ease-in-out infinite' }}
          />
          <path
            d="M0,190 C360,170 540,200 900,185 C1080,175 1320,195 1440,180 L1440,200 L0,200 Z"
            fill={isDark ? 'color-mix(in srgb, var(--rd-primary) 18%, transparent)' : 'color-mix(in srgb, var(--rd-primary) 15%, transparent)'}
            style={{ animation: 'waveMove3 3.5s ease-in-out infinite' }}
          />
          <path
            d="M0,140 C240,180 480,100 720,130 C960,160 1200,90 1440,110"
            fill="none"
            stroke={isDark ? 'color-mix(in srgb, var(--rd-primary) 25%, transparent)' : 'color-mix(in srgb, var(--rd-primary) 20%, transparent)'}
            strokeWidth="2"
            style={{ animation: 'waveMove1 2.8s ease-in-out infinite' }}
          />
          <path
            d="M0,120 C300,80 600,150 900,100 C1100,70 1300,130 1440,90"
            fill="none"
            stroke={isDark ? 'color-mix(in srgb, var(--rd-primary) 15%, transparent)' : 'color-mix(in srgb, var(--rd-primary) 12%, transparent)'}
            strokeWidth="1.5"
            style={{ animation: 'waveMove2 3.2s ease-in-out infinite' }}
          />
        </svg>
      </Box>

      <Container
        maxWidth={false}
        disableGutters
        sx={{
          // 40px = vertikales Padding des main-Containers in App.js
          height: 'calc(100vh - var(--rd-header-height, 138px) - 40px)',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          zIndex: 1,
          px: { xs: 2, sm: 3 },
          py: 2,
          boxSizing: 'border-box'
        }}
      >
        <Box
          sx={{
            width: '100%',
            animation: 'fadeInUp 0.6s ease-out',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0
          }}
        >
          <Box
            sx={{
              mb: 2,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap'
            }}
          >
            <Typography
              variant="h5"
              component="h1"
              sx={{
                fontWeight: 500,
                color: isDark ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.85)',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
                flex: 1,
                minWidth: 0
              }}
            >
              {getGreeting()},
              <Box component="span" sx={{ color: 'var(--rd-primary)', fontWeight: 600 }}>
                {getFullName(currentUser)}
              </Box>
            </Typography>
            <Tooltip
              title={
                isEmpty
                  ? t('dashboard.widgets.emptyHint', 'Ihr Dashboard ist leer. Bitte wählen Sie ein Widget.')
                  : t('dashboard.widgets.addMore', 'Widget hinzufügen')
              }
              placement="left"
            >
              <Fab
                size="medium"
                aria-label={t('dashboard.widgets.add', 'Widgets hinzufügen')}
                onClick={() => setAddDialogOpen(true)}
                sx={{
                  flexShrink: 0,
                  bgcolor: 'var(--rd-primary)',
                  color: 'var(--rd-primary-contrast)',
                  '&:hover': { bgcolor: 'var(--rd-primary-hover)' },
                  boxShadow: 2
                }}
              >
                <AddIcon />
              </Fab>
            </Tooltip>
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
            {!isEmpty && (
              <ResponsiveGridLayout
                className="dashboard-grid"
                rowHeight={FINE_GRID_ROW_HEIGHT}
                margin={[16, 16]}
                containerPadding={[8, 8]}
                layouts={layouts}
                breakpoints={GRID_BREAKPOINTS}
                cols={GRID_COLS}
                draggableHandle=".widget-drag-handle"
                compactType={null}
                preventCollision
                onLayoutChange={handleLayoutChange}
              >
                {widgets.map((wid) => (
                  <Paper
                    key={wid}
                    elevation={isDark ? 6 : 2}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden',
                      borderRadius: 2,
                      border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)',
                      bgcolor: isDark ? 'grey.900' : 'background.paper'
                    }}
                  >
                    <Box
                      className="widget-drag-handle"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 1,
                        py: 0.5,
                        borderBottom: 1,
                        borderColor: 'divider',
                        cursor: 'grab',
                        flexShrink: 0,
                        bgcolor: isDark ? 'grey.900' : 'grey.50'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                        <DragIndicatorIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                        <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600 }}>
                          {widgetTitle(wid)}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        aria-label={t('dashboard.widgets.remove', 'Widget entfernen')}
                        onClick={() => removeWidget(wid)}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <WidgetBodySizer widgetId={wid}>{renderWidgetInner(wid)}</WidgetBodySizer>
                  </Paper>
                ))}
              </ResponsiveGridLayout>
            )}
          </Box>
        </Box>
      </Container>

      {/* ── Compact Add-Widget Dialog ── */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>{t('dashboard.widgets.dialogTitle', 'Widgets hinzufügen')}</DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t('dashboard.widgets.dialogSubtitle', 'Wählen Sie ein Widget aus.')}
          </Typography>
          <List dense disablePadding>
            {allWidgetOptions.map((opt) => {
              const alreadyAdded = widgets.includes(opt.id);
              return (
                <ListItem
                  key={opt.id}
                  disablePadding
                  secondaryAction={
                    <Button
                      size="small"
                      variant={alreadyAdded ? 'outlined' : 'contained'}
                      disabled={alreadyAdded}
                      onClick={() => addWidget(opt.id)}
                      sx={!alreadyAdded ? { bgcolor: 'var(--rd-primary)', '&:hover': { bgcolor: 'var(--rd-primary-hover)' }, minWidth: 90 } : { minWidth: 90 }}
                    >
                      {alreadyAdded ? t('dashboard.widgets.added', 'Hinzugefügt') : t('dashboard.widgets.add', 'Hinzufügen')}
                    </Button>
                  }
                  sx={{ py: 0.75, pr: 14 }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {opt.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={widgetTitle(opt.id)}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                  />
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>{t('common.close', 'Schließen')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Dashboard;
